import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { TOAST_CONFIG } from '../../core/toasts/toast-config.model';
import { Country } from './models/country.model';
import { UserMarketsSettings } from './models/user-markets-settings.model';
import { NotificationSettings } from './notification-settings.model';

@Injectable({ providedIn: 'root' })
export class UserSettingsService {
  private countriesEndpoint = '/api/Countries';
  private settingsEndpoint = '/api/NotificationSettings';
  private marketsSettingsEndpoint = '/api/UserMarketsSettings';

  constructor(private http: HttpClient) {}

  getCountries(): Observable<Country[]> {
    return this.http.get<Country[]>(`${environment.apiUrl}${this.countriesEndpoint}`);
  }

  async createNotificationSettings(settings: NotificationSettings): Promise<number> {
    const settings$: Observable<number> = this.http.post<number>(
      `${environment.apiUrl}${this.settingsEndpoint}`,
      settings,
      {
        context: new HttpContext().set(TOAST_CONFIG, {
          successMessage: { text: 'Notification settings created.' },
        }),
      }
    );
    return await firstValueFrom(settings$);
  }

  async updateNotificationSettings(settings: NotificationSettings): Promise<number> {
    const put$: Observable<number> = this.http.put<number>(
      `${environment.apiUrl}${this.settingsEndpoint}/${settings.id}`,
      settings,
      {
        context: new HttpContext().set(TOAST_CONFIG, {
          successMessage: { text: 'Notification settings updated.' },
        }),
      }
    );
    return await firstValueFrom(put$);
  }

  async getNotificationSettingsByUser(user: string): Promise<NotificationSettings> {
    const settings$: Observable<NotificationSettings> = this.http.get<NotificationSettings>(
      `${environment.apiUrl}${this.settingsEndpoint}?user=${user}`
    );
    return await firstValueFrom(settings$);
  }

  async createUserMarketsSettings(payload: {
    operatingCountryIds: number[];
  }): Promise<UserMarketsSettings> {
    const result$: Observable<UserMarketsSettings> = this.http.post<UserMarketsSettings>(
      `${environment.apiUrl}${this.marketsSettingsEndpoint}`,
      payload,
      {
        context: new HttpContext().set(TOAST_CONFIG, {
          successMessage: { text: 'Markets settings saved.' },
        }),
      }
    );
    return await firstValueFrom(result$);
  }

  async updateUserMarketsSettings(payload: { operatingCountryIds: number[] }): Promise<void> {
    const put$: Observable<void> = this.http.put<void>(
      `${environment.apiUrl}${this.marketsSettingsEndpoint}`,
      payload,
      {
        context: new HttpContext().set(TOAST_CONFIG, {
          successMessage: { text: 'Markets settings updated.' },
        }),
      }
    );
    await firstValueFrom(put$);
  }

  async getUserMarketsSettings(): Promise<UserMarketsSettings | null> {
    const settings$: Observable<UserMarketsSettings | null> = this.http.get<UserMarketsSettings>(
      `${environment.apiUrl}${this.marketsSettingsEndpoint}`
    );
    return await firstValueFrom(settings$);
  }
}
